import type { Fn } from '@vueuse/core';
import { useTimeoutFn } from '@vueuse/core';
import each from 'lodash/each';
import { defineStore } from 'pinia';

import {
  GlobalChoices,
  SimpleChoice,
  SimpleChoiceType,
  SpartenSpecificChoices,
} from '@/features/ChoicesLists/Simple/types';
import { useApi } from '@/services/api/useApi';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import mapAssoc from '@/utils/mapAssoc/mapAssoc';
import { useGlobalStore } from '@/features/Global/store';

interface State {
  loadingCounter: number;
  loadingQueue: Set<SimpleChoiceType>;
  pendingLoading: {
    isPending: boolean;
    stop: Fn;
  };
  data: {
    [k: string]: SimpleChoice;
  };
  loadingSynchronously: boolean;
}

export const useSimpleChoicesStore = defineStore('ChoicesListSimpleStore', {
  state: (): State => ({
    loadingCounter: 0,
    loadingQueue: new Set(),
    pendingLoading: {
      isPending: false,
      stop: (): void => undefined,
    },
    data: {},
    loadingSynchronously: false,
  }),

  actions: {
    loadChoice(choice: SimpleChoiceType) {
      if (Object.prototype.hasOwnProperty.call(this.data, choice)) {
        return;
      }

      const api = useApi();

      this.loadingQueue.add(choice);

      if (this.pendingLoading.isPending) {
        this.pendingLoading.stop();
      }

      const { isPending, stop, start } = useTimeoutFn(async () => {
        this.loadingCounter += 1;
        try {
          if (this.loadingQueue.size === 0) {
            return;
          }

          const [, response] = api.choicesSimple.loadChoices(
            Array.from(this.loadingQueue),
          );

          const result = await response;
          if (result === null) {
            throw new VglError(
              VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE,
              {
                additionalErrorMessage: 'choices simple -> loadChoices',
              },
            );
          }

          this.storeLoadedChoices(result.choices);

          this.loadingQueue.clear();
        } catch (error) {
          throw new VglError(VGL_ERROR_CODE.CHOICES_SIMPLE_LOADING_FAILED, {
            additionalErrorMessage: `Error: ${getErrorMessage(error)}`,
            cause: error,
          });
        } finally {
          this.loadingCounter -= 1;
        }
      }, 300);

      this.pendingLoading.isPending = isPending.value;
      this.pendingLoading.stop = stop;
      start();
    },

    async loadChoicesAsynchronously(choices: SimpleChoiceType[]) {
      const choicesToLoad: SimpleChoiceType[] = choices.filter(
        (choice) => !Object.prototype.hasOwnProperty.call(this.data, choice),
      );

      if (choicesToLoad.length === 0) return;

      const api = useApi();
      const [, request] = api.choicesSimple.loadChoices(choicesToLoad);

      const result = await request;
      if (result === null) {
        throw new VglError(VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE, {
          additionalErrorMessage: 'choices simple -> loadChoices async',
        });
      }

      this.storeLoadedChoices(result.choices);
    },

    loadChoicesSynchronously(choices: SimpleChoiceType[]) {
      this.loadingSynchronously = true;

      const choicesToLoad: SimpleChoiceType[] = choices.filter(
        (choice) => !Object.prototype.hasOwnProperty.call(this.data, choice),
      );

      if (choicesToLoad.length === 0) {
        this.loadingSynchronously = false;
        return;
      }

      const api = useApi();
      const [, result] = api.choicesSimple.loadChoices(choicesToLoad);

      result
        .then((data) => {
          if (data !== null) {
            this.storeLoadedChoices(data.choices);
          }
        })
        .finally(() => {
          this.loadingSynchronously = false;
        });
    },

    storeLoadedChoices(choices: SimpleChoice[]) {
      const choicesByName = mapAssoc(choices, 'name') as Record<
        string,
        SimpleChoice
      >;

      this.data = {
        ...this.data,
        ...choicesByName,
      };
    },

    async preloadSpartenChoices() {
      const choices = [
        ...GlobalChoices,
        ...(SpartenSpecificChoices[useGlobalStore().getSparte] ?? []),
      ];

      await this.loadChoicesAsynchronously(choices);
    },
  },

  getters: {
    isLoading: (state) => state.loadingCounter > 0,

    meta: (state) => {
      const result: Record<string, SimpleChoice['meta']> = {};
      each(state.data, (value, key) => {
        if (value.meta !== undefined && !Array.isArray(value.meta)) {
          result[key] = value.meta;
        }
      });
      return result;
    },

    getLabel: (state) => (choice: string, value: string | number) => {
      if (state.data[choice] !== undefined) {
        return state.data[choice].values.find((item) => item.value === value)
          ?.label;
      }
      return undefined;
    },
  },
});
